import {
  DeleteOutlined,
  EnvironmentOutlined,
  FilterOutlined,
  LaptopOutlined,
  LockOutlined,
  RightOutlined,
  SearchOutlined,
  SendOutlined,
  UnlockOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import { Button, Drawer, Input, Popconfirm, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DataTable, Loader } from '@kolab/dms-base/components/ui'
import { useInfiniteScroll } from '@kolab/dms-base/hooks'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as devicesApi from '../../api/devices.api'
import DeviceListMapView from '../../components/devices/DeviceListMapView'
import DeviceStatusBadge from '../../components/devices/DeviceStatusBadge'
import LockDeviceModal from '../../components/devices/LockDeviceModal'
import NotifyDeviceModal from '../../components/devices/NotifyDeviceModal'
import UnlockDeviceModal from '../../components/devices/UnlockDeviceModal'
import type { ColumnConfig, DeviceFilters } from '../../components/devices/filters'
import {
  ColumnSelector,
  DEFAULT_FILTERS,
  DeviceFilterSidebar,
} from '../../components/devices/filters'
import { useDeleteDevice } from '../../hooks/useDevices'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import type { Device } from '../../types/device.types'
import { QUERY_KEYS, ROUTES } from '../../utils/constants'
import { formatDate } from '../../utils/formatters'
import { Can, P } from '../../rbac'

const ALL_COLUMNS: ColumnConfig[] = [
  { key: 'device', label: 'Device' },
  { key: 'lastConnection', label: 'Last connection' },
  { key: 'createdAt', label: 'Registered Date' },
  { key: 'actions', label: 'Actions' },
]

const DEFAULT_VISIBLE_COLUMNS = ['device', 'lastConnection', 'createdAt', 'actions']

function isWithinPeriod(dateStr: string | null, period: string): boolean {
  if (!dateStr) return period === 'any'
  if (period === 'any') return true

  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = diffMs / (1000 * 60 * 60 * 24)

  switch (period) {
    case 'today':
      return diffDays < 1
    case 'yesterday':
      return diffDays < 2
    case 'week':
      return diffDays <= 7
    case 'month':
      return diffDays <= 30
    case 'year':
      return diffDays <= 365
    default:
      return true
  }
}

export default function DeviceListPage() {
  const navigate = useNavigate()
  const deleteDevice = useDeleteDevice()
  const [notifyModal, setNotifyModal] = useState({ open: false, deviceId: '' })
  const [lockModal, setLockModal] = useState({ open: false, deviceId: '' })
  const [unlockModal, setUnlockModal] = useState({ open: false, deviceId: '' })

  const [viewMode, setViewMode] = useState<'list' | 'map'>('list')
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState<DeviceFilters>(DEFAULT_FILTERS)
  const [searchQuery, setSearchQuery] = useState('')
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE_COLUMNS)
  const isMobile = useMediaQuery('(max-width: 768px)')

  const { items, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteScroll<Device>({
      queryKey: [...QUERY_KEYS.devices, 'cursor'],
      queryFn: devicesApi.listCursor,
    })

  const filteredItems = useMemo(() => {
    return items.filter((device) => {
      if (filters.connectionStatus === 'online' && !device.isOnline) return false
      if (filters.connectionStatus === 'offline' && device.isOnline) return false
      if (!isWithinPeriod(device.lastPingAt, filters.lastConnection)) return false
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        const matchesId = device.device_id.toLowerCase().includes(q)
        const matchesIp = device.ipAddress?.toLowerCase().includes(q)
        const matchesName = device.pingInfo?.ComputerName?.toLowerCase().includes(q)
        if (!matchesId && !matchesIp && !matchesName) return false
      }
      return true
    })
  }, [items, filters, searchQuery])

  if (isLoading) return <Loader />

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {/* Filter Sidebar - desktop: inline push, mobile: drawer overlay */}
      {showFilters && viewMode === 'list' && !isMobile && (
        <DeviceFilterSidebar
          filters={filters}
          onFilterChange={setFilters}
          onClose={() => setShowFilters(false)}
        />
      )}
      {isMobile && (
        <Drawer
          open={showFilters && viewMode === 'list'}
          onClose={() => setShowFilters(false)}
          placement="left"
          styles={{ body: { padding: 0 }, wrapper: { width: 280 } }}
          closable={false}
        >
          <DeviceFilterSidebar
            filters={filters}
            onFilterChange={setFilters}
            onClose={() => setShowFilters(false)}
          />
        </Drawer>
      )}

      {/* Main Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Top Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          {/* View Toggle */}
          <div
            style={{
              display: 'flex',
              borderRadius: 8,
              overflow: 'hidden',
              border: '1px solid #e2e8f0',
            }}
          >
            <Button
              type={viewMode === 'list' ? 'primary' : 'default'}
              icon={<UnorderedListOutlined />}
              onClick={() => setViewMode('list')}
              style={{
                borderRadius: 0,
                borderRight: 'none',
                fontWeight: viewMode === 'list' ? 600 : 400,
              }}
            >
              {!isMobile && 'List view'}
            </Button>
            <Button
              type={viewMode === 'map' ? 'primary' : 'default'}
              icon={<EnvironmentOutlined />}
              onClick={() => setViewMode('map')}
              style={{
                borderRadius: 0,
                fontWeight: viewMode === 'map' ? 600 : 400,
              }}
            >
              {!isMobile && 'Map view'}
            </Button>
          </div>
        </div>

        {/* Filter Row */}
        {viewMode === 'list' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 16,
              flexWrap: 'wrap',
            }}
          >
            {isMobile ? (
              <Button
                icon={<FilterOutlined />}
                onClick={() => setShowFilters(!showFilters)}
                type={showFilters ? 'primary' : 'default'}
                style={{ borderRadius: 8 }}
              />
            ) : (
              <Button
                icon={<FilterOutlined />}
                onClick={() => setShowFilters(!showFilters)}
                type={showFilters ? 'primary' : 'default'}
                style={{ borderRadius: 8 }}
              >
                Quick filters
              </Button>
            )}
            <Input
              placeholder={isMobile ? 'Search...' : 'Search by name, OS, type, serial number...'}
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                maxWidth: isMobile ? 200 : 360,
                borderRadius: 8,
                flex: isMobile ? 1 : undefined,
              }}
              allowClear
            />
          </div>
        )}

        {/* Device Count + Column Selector */}
        {viewMode === 'list' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}
          >
            <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
              Showing {filteredItems.length} device{filteredItems.length !== 1 ? 's' : ''}
            </p>
            <ColumnSelector
              allColumns={ALL_COLUMNS}
              visibleColumns={visibleColumns}
              onChange={setVisibleColumns}
            />
          </div>
        )}

        {/* Content */}
        {viewMode === 'map' ? (
          <DeviceListMapView devices={filteredItems} />
        ) : (
          <>
            <DataTable<Device>
              columns={buildColumns(
                navigate,
                deleteDevice,
                {
                  onNotify: (deviceId) => setNotifyModal({ open: true, deviceId }),
                  onLock: (deviceId) => setLockModal({ open: true, deviceId }),
                  onUnlock: (deviceId) => setUnlockModal({ open: true, deviceId }),
                },
                visibleColumns
              )}
              dataSource={filteredItems}
              rowKey="device_id"
              loading={deleteDevice.isPending}
              infiniteScroll={{ isFetchingNextPage, hasNextPage, fetchNextPage }}
            />
          </>
        )}

        <NotifyDeviceModal
          deviceId={notifyModal.deviceId}
          open={notifyModal.open}
          onClose={() => setNotifyModal({ open: false, deviceId: '' })}
        />

        <LockDeviceModal
          deviceId={lockModal.deviceId}
          open={lockModal.open}
          onClose={() => setLockModal({ open: false, deviceId: '' })}
        />

        <UnlockDeviceModal
          deviceId={unlockModal.deviceId}
          open={unlockModal.open}
          onClose={() => setUnlockModal({ open: false, deviceId: '' })}
        />
      </div>
    </div>
  )
}

function buildColumns(
  navigate: ReturnType<typeof useNavigate>,
  deleteDevice: ReturnType<typeof useDeleteDevice>,
  actions: {
    onNotify: (deviceId: string) => void
    onLock: (deviceId: string) => void
    onUnlock: (deviceId: string) => void
  },
  visibleColumns: string[]
): ColumnsType<Device> {
  const allColumns: ColumnsType<Device> = [
    {
      title: 'Device',
      key: 'device',
      width: 280,
      sorter: (a, b) => a.device_id.localeCompare(b.device_id),
      render: (_, record) => {
        const computerName = record.pingInfo?.ComputerName
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
              <LaptopOutlined style={{ color: '#475569', fontSize: 14 }} />
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#1e293b',
                  cursor: 'pointer',
                }}
                onClick={() => navigate(ROUTES.deviceDetails(record.device_id))}
              >
                {computerName || record.device_id}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 22 }}>
              <DeviceStatusBadge isOnline={record.isOnline} />
              {computerName && (
                <span style={{ fontSize: 11, color: '#94a3b8' }}>
                  {record.pingInfo?.OSVersion || ''}
                </span>
              )}
            </div>
          </div>
        )
      },
    },
    {
      title: 'Last connection',
      key: 'lastConnection',
      width: 200,
      sorter: (a, b) => {
        if (!a.lastPingAt && !b.lastPingAt) return 0
        if (!a.lastPingAt) return 1
        if (!b.lastPingAt) return -1
        return new Date(b.lastPingAt).getTime() - new Date(a.lastPingAt).getTime()
      },
      defaultSortOrder: 'ascend',
      render: (_, record) => (
        <span style={{ fontSize: 13, color: '#475569' }}>
          {record.lastPingAt ? formatDate(record.lastPingAt) : 'Never'}
        </span>
      ),
    },
    {
      title: 'Registered At',
      key: 'createdAt',
      width: 200,
      sorter: (a, b) => {
        if (!a.createdAt && !b.createdAt) return 0
        if (!a.createdAt) return 1
        if (!b.createdAt) return -1
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      },
      defaultSortOrder: 'ascend',
      render: (_, record) => (
        <span style={{ fontSize: 13, color: '#475569' }}>
          {record.createdAt ? formatDate(record.createdAt) : 'Never'}
        </span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
          <Can permission={P.device_operations.device.notify}>
            <Tooltip title="Send Message">
              <Button
                type="text"
                size="small"
                icon={<SendOutlined />}
                onClick={() => actions.onNotify(record.device_id)}
              />
            </Tooltip>
          </Can>
          <Can permission={P.device_operations.device.lock}>
            <Tooltip title="Lock Device">
              <Button
                type="text"
                size="small"
                icon={<LockOutlined />}
                onClick={() => actions.onLock(record.device_id)}
                style={{ color: !record.isOnline ? undefined : '#ef4444' }}
              />
            </Tooltip>
          </Can>
          <Can permission={P.device_operations.device.unlock}>
            <Tooltip title="Unlock Device">
              <Button
                type="text"
                size="small"
                icon={<UnlockOutlined />}
                onClick={() => actions.onUnlock(record.device_id)}
                style={{ color: !record.isOnline ? undefined : '#10b981' }}
              />
            </Tooltip>
          </Can>
          <Can permission={P.device_operations.device.delete}>
            <Popconfirm
              title="Delete this device?"
              description="This action cannot be undone."
              onConfirm={() => deleteDevice.mutate(record.device_id)}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button type="text" size="small" icon={<DeleteOutlined />} />
            </Popconfirm>
          </Can>
          <Button
            type="text"
            size="small"
            icon={<RightOutlined />}
            onClick={() => navigate(ROUTES.deviceDetails(record.device_id))}
          />
        </div>
      ),
    },
  ]

  return allColumns.filter((col) => {
    const key = col.key as string
    if (key === 'actions') return visibleColumns.includes('actions')
    return visibleColumns.includes(key)
  })
}
